import { useSyncExternalStore } from 'react';
import { scheduleFor, type ApiHousehold, type Crew, type Snapshot } from '@hogar/shared';

/**
 * The house and the garden (Mike, 2026-10-14). Once a home has a garden, the owner's tabs show a
 * "House | Garden" switch; the choice is remembered across tabs while the app is open.
 */
let current: Crew = 'house';
const listeners = new Set<() => void>();
export function setCrew(crew: Crew) {
  current = crew;
  for (const l of listeners) l();
}
export function useCrew(): Crew {
  return useSyncExternalStore((l) => (listeners.add(l), () => listeners.delete(l)), () => current);
}

/** Whether the home has a garden: a garden area, or a gardener. */
export function hasGarden(snap: Pick<Snapshot, 'rooms' | 'staff'>): boolean {
  return snap.rooms.some((r) => r.crew === 'garden') || snap.staff.some((s) => s.crew === 'garden');
}

/** One crew's slice of the home: its areas, tasks, supplies, people, and schedule. */
export function crewSlice(snap: Snapshot & { household: ApiHousehold }, crew: Crew) {
  const rooms = snap.rooms.filter((r) => r.crew === crew);
  const ids = new Set(rooms.map((r) => r.id));
  return {
    rooms,
    tasks: snap.tasks.filter((t) => ids.has(t.roomId)),
    products: snap.products.filter((p) => p.crew === crew),
    staff: snap.staff.filter((s) => s.crew === crew),
    reports: snap.reports.filter((r) => r.crew === crew),
    schedule: scheduleFor(snap.household, crew),
  };
}
